import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import createApiClient from "../../utils/createApiClient";

// Interface for utility data
interface UtilityData {
  utilityId: string;
  utilityName: string;
  state: string;
}

// Interface for utility API response
export interface UtilityApiResponse {
  success: boolean;
  message: string;
  data?: {
    utilities: UtilityData[];
  };
}

// Interface for schedule data
interface ScheduleData {
  scheduleId: string;
  utilityId: string;
  scheduleName: string;
  scheduleDescription: string;
  useType: string;
  minDemand: string;
  maxDemand: string;
  minUsage: string;
  maxUsage: string;
  effectiveDate: string;
  optionType: string;
  optionDescription: string;
  utilityName: string;
  state: string;
  pending: string;
}

// Interface for schedule API response
export interface ScheduleApiResponse {
  success: boolean;
  message: string;
  data?: {
    schedule: ScheduleData;
  };
}

// Interface for energy time table data
interface EnergyTimeData {
  pending: string;
  scheduleId: string;
  description: string;
  rateKWh: string;
  minKV: string;
  maxKV: string;
  season: string;
  startDate: string;
  endDate: string;
  timeOfDay: string;
  startTime: string;
  endTime: string;
  minTemp: string;
  maxTemp: string;
  daysAppDesc: string;
}

// Interface for incremental energy table data
interface IncrementalEnergyData {
  pending: string;
  scheduleId: string;
  description: string;
  rateKWh: string;
  startKWh: string;
  endKWh: string;
  season: string;
  startDate: string;
  endDate: string;
  timeOfDay: string;
  startTime: string;
  endTime: string;
  minTemp: string;
  maxTemp: string;
  daysAppDesc: string;
}

// Interface for energy table data
interface EnergyData {
  pending: string;
  scheduleId: string;
  description: string;
  rateKWh: string;
  minKV: string;
  maxKV: string;
}

// Interface for energy API response
export interface EnergyApiResponse {
  success: boolean;
  message: string;
  data?: {
    energyTimeTable: EnergyTimeData[];
    incrementalEnergyTable: IncrementalEnergyData[];
    energyTable: EnergyData[];
  };
}

function isAxiosError(error: unknown): error is import("axios").AxiosError {
  return (error as import("axios").AxiosError).isAxiosError !== undefined;
}

// GET UTILITY DATA BY ZIP CODE
export const getUtilityData = createAsyncThunk<
  UtilityApiResponse,
  string,
  { rejectValue: string }
>("common/getUtilityData", async (zipCode, { rejectWithValue }) => {
  try {
    // Validate zip code
    if (!/^\d{5}$/.test(zipCode)) {
      toast.error("Invalid zip code. Must be a 5-digit number");
      return rejectWithValue("Invalid zip code. Must be a 5-digit number");
    }

    const response = await createApiClient.get<UtilityApiResponse>(
      `/utility/${zipCode}`
    );

    if (response.data.success) {
      toast.success(
        response.data.message || "Utility data retrieved successfully"
      );
      return response.data;
    } else {
      toast.error(response.data.message || "Failed to retrieve utility data");
      return rejectWithValue(
        response.data.message || "Failed to retrieve utility data"
      );
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";
    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as UtilityApiResponse).message || "Unknown error";
      if (error.response.status === 504) {
        errorMessage = "Request to API timed out";
      } else if (error.response.status === 503) {
        errorMessage = "Failed to connect to API";
      } else if (error.response.status === 404) {
        errorMessage = "No utility data found for the provided zip code";
      }
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

// GET SCHEDULE DATA BY UTILITY ID
export const getScheduleData = createAsyncThunk<
  ScheduleApiResponse,
  string,
  { rejectValue: string }
>("common/getScheduleData", async (utilityId, { rejectWithValue }) => {
  try {
    // Validate utility ID
    if (!/^\d+$/.test(utilityId)) {
      toast.error("Invalid utility ID. Must be a number");
      return rejectWithValue("Invalid utility ID. Must be a number");
    }

    const response = await createApiClient.get<ScheduleApiResponse>(
      `/schedule/${utilityId}`
    );

    if (response.data.success) {
      toast.success(
        response.data.message || "Schedule data retrieved successfully"
      );
      return response.data;
    } else {
      toast.error(response.data.message || "Failed to retrieve schedule data");
      return rejectWithValue(
        response.data.message || "Failed to retrieve schedule data"
      );
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";
    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as ScheduleApiResponse).message || "Unknown error";
      if (error.response.status === 504) {
        errorMessage = "Request to API timed out";
      } else if (error.response.status === 503) {
        errorMessage = "Failed to connect to API";
      } else if (error.response.status === 404) {
        errorMessage = "No schedule data found for the provided utility ID";
      }
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

// GET ENERGY DATA BY SCHEDULE ID
export const getEnergyData = createAsyncThunk<
  EnergyApiResponse,
  string,
  { rejectValue: string }
>("common/getEnergyData", async (scheduleId, { rejectWithValue }) => {
  try {
    // Validate schedule ID
    if (!/^\d+$/.test(scheduleId)) {
      toast.error("Invalid schedule ID. Must be a number");
      return rejectWithValue("Invalid schedule ID. Must be a number");
    }

    const response = await createApiClient.get<EnergyApiResponse>(
      `common/energy/${scheduleId}`
    );

    if (response.data.success) {
      return response.data;
    } else {
      toast.error(response.data.message || "Failed to retrieve energy data");
      return rejectWithValue(
        response.data.message || "Failed to retrieve energy data"
      );
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";
    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as EnergyApiResponse).message || "Unknown error";
      if (error.response.status === 504) {
        errorMessage = "Request to API timed out";
      } else if (error.response.status === 503) {
        errorMessage = "Failed to connect to API";
      } else if (error.response.status === 404) {
        errorMessage = "No energy data found for the provided schedule ID";
      } else if (error.response.status === 400) {
        errorMessage = "Invalid energy data structure";
      }
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});
